import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  BulkSetInventoryStartingValuesData,
  BulkSetInventoryStartingValuesResponse,
  CorrectInventoryStartingValueData,
  CorrectInventoryStartingValueResponse,
  EditInventoryStartingValueResponse,
  InventoryStartingValue,
  InventoryStartingValueResponse,
  SetInventoryStartingValueData,
  StartingValueCorrectionPreview,
  StartingValueCorrectionPreviewResponse,
  VoidInventoryStartingValueData,
} from '../types/inventoryStartingValue';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getItemStartingValue = async (
  itemId: number
): Promise<InventoryStartingValue | null> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_STARTING_VALUES.GET_BY_ITEM}/${itemId}/starting-value`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch starting value');
  }

  const result: InventoryStartingValueResponse = await response.json();
  return result.data;
};

export const setItemStartingValue = async (
  itemId: number,
  data: SetInventoryStartingValueData
): Promise<InventoryStartingValue> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_STARTING_VALUES.GET_BY_ITEM}/${itemId}/starting-value`,
    {
      method: 'PUT',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to set starting value');
  }

  const result: InventoryStartingValueResponse = await response.json();
  return result.data;
};

export const editItemStartingValue = async (
  itemId: number,
  data: SetInventoryStartingValueData
): Promise<EditInventoryStartingValueResponse> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_STARTING_VALUES.GET_BY_ITEM}/${itemId}/starting-value`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to edit starting value');
  }

  return response.json();
};

export const bulkSetStartingValues = async (
  data: BulkSetInventoryStartingValuesData
): Promise<BulkSetInventoryStartingValuesResponse> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_STARTING_VALUES.BULK}`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to bulk set starting values');
  }

  return response.json();
};

export const voidItemStartingValue = async (
  itemId: number,
  data?: VoidInventoryStartingValueData
): Promise<InventoryStartingValue> => {
  const trimmedReason = data?.void_reason?.trim();
  const body = trimmedReason ? JSON.stringify({ void_reason: trimmedReason }) : undefined;

  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_STARTING_VALUES.VOID_BY_ITEM}/${itemId}/starting-value/void`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body ? { body } : {}),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to void starting value');
  }

  const result: InventoryStartingValueResponse = await response.json();
  return result.data;
};

export const previewItemStartingValueCorrection = async (
  itemId: number,
  data: CorrectInventoryStartingValueData
): Promise<StartingValueCorrectionPreview> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_STARTING_VALUES.CORRECTION_PREVIEW_BY_ITEM}/${itemId}/starting-value/correction-preview`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to preview starting value correction');
  }

  const result: StartingValueCorrectionPreviewResponse = await response.json();
  return result.data;
};

export const correctItemStartingValue = async (
  itemId: number,
  data: CorrectInventoryStartingValueData
): Promise<CorrectInventoryStartingValueResponse> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_STARTING_VALUES.CORRECT_BY_ITEM}/${itemId}/starting-value/correct`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to correct starting value');
  }

  return response.json();
};
